import React from 'react';
import { AlertCircle } from 'lucide-react';
import { getScoreBandColor, getScoreBandLabel, getScoreBandClass } from '../../design-system/tokens';

/**
 * Reusable HQ score display.
 * Sizes: 'sm' (inline), 'md' (card list), 'lg' (hero/detail).
 */
export function ScoreDisplay({ score, showBand = true, showRaw, rawScore, size = 'lg', className = '' }) {
  const color = getScoreBandColor(score ?? 0);
  const band = getScoreBandLabel(score ?? 0);
  const cssClass = getScoreBandClass(score ?? 0);

  if (size === 'sm') {
    return (
      <span className={`font-bold ${cssClass} ${className}`} data-testid="score-display-sm">
        {(score ?? 0).toFixed(1)}
      </span>
    );
  }

  if (size === 'md') {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid="score-display-md">
        <span className={`text-lg font-bold ${cssClass}`}>{(score ?? 0).toFixed(1)}</span>
        {showBand && <span className="text-xs text-[var(--msml-text-muted)]">{band}</span>}
      </div>
    );
  }

  return (
    <div className={`text-center ${className}`} data-testid="score-display-lg">
      <div className={`msml-score ${cssClass}`}>{Math.round(score ?? 0)}</div>
      {showBand && (
        <p className="text-sm mt-1" style={{ color }}>{band}</p>
      )}
      {showRaw && rawScore != null && rawScore !== score && (
        <p className="text-xs text-[var(--msml-text-muted)] mt-1">Raw: {Math.round(rawScore)}</p>
      )}
    </div>
  );
}

/** Renders caps and flags beneath a score. */
export function ScoreFlags({ flags, capsApplied = [] }) {
  const hasFlags = flags?.high_risk || flags?.high_dependency || flags?.overload;
  if (!capsApplied.length && !hasFlags) return null;

  return (
    <div className="space-y-2" data-testid="score-flags">
      {capsApplied.length > 0 && (
        <div className="flex flex-wrap gap-1 justify-center">
          {capsApplied.map((cap, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]">
              <AlertCircle className="w-3 h-3" />{cap}
            </span>
          ))}
        </div>
      )}
      {hasFlags && (
        <div className="pt-2 border-t border-white/10 space-y-1">
          {flags.high_risk && (
            <p className="text-xs text-[var(--msml-danger)] flex items-center justify-center gap-1">
              <AlertCircle className="w-3 h-3" /> High risk detected
            </p>
          )}
          {flags.high_dependency && (
            <p className="text-xs text-[var(--msml-warning)] flex items-center justify-center gap-1">
              <AlertCircle className="w-3 h-3" /> Creates dependency
            </p>
          )}
          {flags.overload && (
            <p className="text-xs text-[var(--msml-warning)] flex items-center justify-center gap-1">
              <AlertCircle className="w-3 h-3" /> Cognitive overload
            </p>
          )}
        </div>
      )}
    </div>
  );
}
